// src/hooks/usePresence.js
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { collection, doc, onSnapshot, setDoc, Timestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase-config";

// 変更されない空オブジェクト（依存配列の無駄な変化を防止）
const EMPTY_OBJ = Object.freeze({});

// 各タブ（セッション）を一意に識別する ID を作成
function genSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * usePresenceHeartbeat
 * - Firestore の presence コレクションへ「オンライン心拍（lastSeen）」を書き込む
 * - タブを閉じる/ログアウト時は online=false で更新
 * - user.uid は「アプリのユーザーID」（Firebase Auth の uid に依存しない）
 */
export function usePresenceHeartbeat(user, options = {}) {
  const intervalMs = options.intervalMs ?? 25000;   // 心拍間隔 25s
  const staleMs    = options.staleMs    ?? 120000;  // オンライン判定の賞味期限 2分（見やすさ優先）
  const extraObj   = options.extra      ?? EMPTY_OBJ;

  // 表示名／アイコンなど（なくても可）
  const userUid = user && user.uid ? String(user.uid) : null;
  const displayName =
    user && (user.fullName || user.nameRomaji || user.displayName)
      ? (user.fullName || user.nameRomaji || user.displayName)
      : null;
  const photoURL = user && (user.avatar || user.photoURL) ? (user.avatar || user.photoURL) : null;

  // extra オブジェクトを安定化（依存配列に直接 object を置かない）
  const extraStr    = useMemo(() => JSON.stringify(extraObj), [extraObj]);
  const extraStable = useMemo(() => (extraStr ? JSON.parse(extraStr) : EMPTY_OBJ), [extraStr]);

  const sessionIdRef = useRef(null);                 // タブ単位のセッションID
  const timerRef     = useRef(null);                 // setInterval のハンドル
  const [isActive, setIsActive] = useState(false);   // 心拍が動作中か

  // 自身の presence ドキュメント参照（例: "userId_sessionId"）
  const presenceDocRef = useMemo(() => {
    if (!userUid) return null;
    if (!sessionIdRef.current) sessionIdRef.current = genSessionId();
    const id = `${userUid}_${sessionIdRef.current}`;
    return doc(collection(db, "presence"), id);
  }, [userUid]);

  // 心拍停止（ログアウトやタブクローズ時に実行）
  const stop = useCallback(async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsActive(false);
    try {
      if (presenceDocRef) {
        await updateDoc(presenceDocRef, {
          online: false,                // ← 即座にオフラインへ
          lastSeen: Timestamp.now(),
          endedAt: Timestamp.now(),
        });
      }
    } catch {
      // タブクローズ時は失敗しても仕方ないので握り潰す
    }
  }, [presenceDocRef]);

  // マウント時に心拍開始／アンマウント時に停止
  useEffect(() => {
    if (!userUid || !presenceDocRef) return;

    let cancelled = false;

    (async () => {
      try {
        // 初回は setDoc（merge）で作成/更新
        await setDoc(
          presenceDocRef,
          {
            userId: userUid,           // デデュープはこの userId 単位で行う
            sessionId: sessionIdRef.current,
            online: true,
            lastSeen: Timestamp.now(),
            displayName,
            photoURL,
            staleMs,                   // 将来サーバ側で参照したい場合に備えて保存
            ...extraStable,            // 例: className, regNumber, role など任意
          },
          { merge: true }
        );
        if (!cancelled) setIsActive(true);
      } catch (e) {
        console.warn("[presence] setDoc error:", e);
      }

      // 心拍ループ（intervalMs ごとに lastSeen を更新）
      if (!cancelled) {
        timerRef.current = setInterval(async () => {
          try {
            if (presenceDocRef) {
              await updateDoc(presenceDocRef, { online: true, lastSeen: Timestamp.now() });
            }
          } catch (e) {
            console.warn("[presence] heartbeat error:", e);
          }
        }, intervalMs);
      }
    })();

    // タブ閉鎖検知（best-effort）
    const onPageHide = () => { stop(); };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);

    return () => {
      cancelled = true;
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [userUid, presenceDocRef, intervalMs, staleMs, extraStable, stop, displayName, photoURL]);

  return { sessionId: sessionIdRef.current, isActive, stop };
}

/**
 * useOnlineCount
 * - presence コレクション全体を監視し、ユーザーID（userId）単位で重複排除してカウント
 * - online が false でない（true/undefined）かつ lastSeen が期限内のものをオンラインとみなす
 */
export function useOnlineCount(staleMs = 120000) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const colRef = collection(db, "presence");
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const now = Date.now();
        const users = new Set();
        snap.forEach((d) => {
          const data = d.data();
          const userId = data?.userId ? String(data.userId) : null;
          const isOnline = data?.online !== false; // online が false でなければオンライン扱い
          const ts = data?.lastSeen;
          const lastSeenMs =
            ts && typeof ts.toMillis === "function" ? ts.toMillis() : (ts ? new Date(ts).getTime() : 0);
          if (userId && isOnline && lastSeenMs > now - staleMs) users.add(userId);
        });
        setCount(users.size);
      },
      (err) => {
        console.warn("[presence] onSnapshot count error:", err);
        setCount(0);
      }
    );
    return () => unsub();
  }, [staleMs]);

  return { count };
}

/**
 * useOnlineUsers
 * - オンラインのユーザー一覧を返す
 * - 同一ユーザーが複数タブを開いている場合は、lastSeen が最新の 1 件に正規化（Map で管理）
 */
export function useOnlineUsers(staleMs = 120000) {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const colRef = collection(db, "presence");
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const now = Date.now();
        const latest = new Map(); // userId -> 最新レコード
        snap.forEach((docSnap) => {
          const d = docSnap.data() || {};
          const userId = d.userId ? String(d.userId) : null;
          const isOnline = d.online !== false;
          const ts = d.lastSeen;
          const lastSeenMs =
            ts && typeof ts.toMillis === "function" ? ts.toMillis() : (ts ? new Date(ts).getTime() : 0);
          if (!userId || !isOnline || lastSeenMs <= now - staleMs) return;

          const prev = latest.get(userId);
          if (!prev || lastSeenMs > prev.lastSeenMs) {
            latest.set(userId, {
              userId,
              displayName: d.displayName || "",
              photoURL: d.photoURL || "",
              className: d.className ?? null,
              regNumber: d.regNumber ?? null,
              role: d.role ?? null,
              lastSeenMs,
            });
          }
        });
        setUsers(Array.from(latest.values()).sort((a, b) => b.lastSeenMs - a.lastSeenMs));
      },
      (err) => {
        console.warn("[presence] onSnapshot list error:", err);
        setUsers([]);
      }
    );
    return () => unsub();
  }, [staleMs]);

  return { users };
}
